import React, { useState, useEffect } from 'react';
import {X, Sparkles, AlertTriangle, Info, Gift, Loader2} from 'lucide-react';import {useNavigate, useLocation} from 'react-router-dom';import {toast} from 'sonner';import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';
import { broadcastAllowedOnCurrentPage } from '@/utils/broadcastFilter';
import { useTranslation } from 'react-i18next';
const STYLE_CONFIG = {
  info: { icon: Info, color: '#6366f1', glow: 'rgba(99,102,241,0.15)' },
  feature: { icon: Sparkles, color: '#22c55e', glow: 'rgba(34,197,94,0.15)' },
  promo: { icon: Gift, color: '#f59e0b', glow: 'rgba(245,158,11,0.15)' },
  warning: { icon: AlertTriangle, color: '#ef4444', glow: 'rgba(239,68,68,0.15)' },
};

export function BroadcastBanner() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/broadcasts/active`, { withCredentials: true });
        const banners = (res.data.broadcasts || []).filter(b =>
          (b.display_locations || []).includes('banner') &&
          broadcastAllowedOnCurrentPage(b, location.pathname)
        );
        setBroadcasts(banners);
      } catch {}
    };
    const t = setTimeout(fetch, 800);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const dismiss = async (id) => {
    try { await axios.post(`${API}/broadcasts/${id}/dismiss`, {}, { withCredentials: true }); } catch {}
    setBroadcasts(prev => prev.filter(b => b.broadcast_id !== id));
  };

  const handleCta = async (b) => {
    if (b.offer_tier && b.offer_duration_days) {
      setAccepting(true);
      try {
        const res = await axios.post(`${API}/broadcasts/${b.broadcast_id}/accept`, {}, { withCredentials: true });
        if (res.data.trial_provisioned) {
          toast.success(`Your ${res.data.duration_days}-day ${res.data.tier} trial is now active!`);
          window.dispatchEvent(new Event('subscription-changed'));
        }
      } catch (err) { toast.error(err?.response?.data?.detail || 'Could not activate offer'); }
      finally { setAccepting(false); }
      setBroadcasts(prev => prev.filter(x => x.broadcast_id !== b.broadcast_id));
      return;
    }
    if (b.cta_link) {
      b.cta_link.startsWith('http') ? window.open(b.cta_link, '_blank') : navigate(b.cta_link);
    }
    try { await axios.post(`${API}/broadcasts/${b.broadcast_id}/accept`, {}, { withCredentials: true }); } catch {}
    setBroadcasts(prev => prev.filter(x => x.broadcast_id !== b.broadcast_id));
  };

  if (broadcasts.length === 0) return null;

  return (
    <div className="space-y-2 mb-3" data-testid="broadcast-banners">
      {broadcasts.map(b => {
        const style = STYLE_CONFIG[b.style] || STYLE_CONFIG.info;
        const Icon = style.icon;
        return (
          <div key={b.broadcast_id} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: `${style.color}10`, border: `1px solid ${style.color}25` }} data-testid={`banner-${b.broadcast_id}`}>
            <Icon className="h-4 w-4 shrink-0" style={{ color: style.color }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium" style={{ color: 'var(--theme-text)' }}>{b.title}</p>
              {b.message && <p className="text-[11px]" style={{ color: 'var(--theme-text-secondary)' }}>{b.message}</p>}
            </div>
            {b.cta_text && (
              <button onClick={() => handleCta(b)} disabled={accepting} className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-medium text-white transition hover:brightness-110 disabled:opacity-60" style={{ background: style.color }} data-testid={`banner-cta-${b.broadcast_id}`}>
                {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : b.cta_text}
              </button>
            )}
            <button onClick={() => dismiss(b.broadcast_id)} className="shrink-0 p-1 rounded-full transition hover:bg-white/10" data-testid={`banner-dismiss-${b.broadcast_id}`}>
              <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-text-secondary)' }} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function BroadcastChatMessages() {
  const {
    t
  } = useTranslation();

  const [broadcasts, setBroadcasts] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/broadcasts/active`, { withCredentials: true });
        const chatMsgs = (res.data.broadcasts || []).filter(b =>
          (b.display_locations || []).includes('chat_message') &&
          broadcastAllowedOnCurrentPage(b, location.pathname)
        );
        setBroadcasts(chatMsgs);
      } catch {}
    };
    const t = setTimeout(fetch, 1000);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const dismiss = async (id) => {
    try { await axios.post(`${API}/broadcasts/${id}/dismiss`, {}, { withCredentials: true }); } catch {}
    setBroadcasts(prev => prev.filter(b => b.broadcast_id !== id));
  };

  if (broadcasts.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="broadcast-chat-messages">
      {broadcasts.map(b => {
        const style = STYLE_CONFIG[b.style] || STYLE_CONFIG.info;
        const Icon = style.icon;
        return (
          <div key={b.broadcast_id} className="flex items-start gap-3 max-w-lg mx-auto" data-testid={`chat-msg-${b.broadcast_id}`}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${style.color}18` }}>
              <Icon className="h-4 w-4" style={{ color: style.color }} />
            </div>
            <div className="flex-1 rounded-2xl rounded-tl-sm px-4 py-3" style={{ background: 'var(--theme-surface)', border: '1px solid var(--theme-border)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--theme-text)' }}>{b.title}</p>
              {b.message && <p className="text-xs leading-relaxed" style={{ color: 'var(--theme-text-secondary)' }}>{b.message}</p>}
              <div className="flex items-center gap-2 mt-2">
                {b.cta_text && (
                  <button onClick={() => b.cta_link && (b.cta_link.startsWith('http') ? window.open(b.cta_link, '_blank') : navigate(b.cta_link))}
                    className="px-3 py-1 rounded-lg text-[11px] font-medium text-white" style={{ background: style.color }}>
                    {b.cta_text}
                  </button>
                )}
                <button onClick={() => dismiss(b.broadcast_id)} className="text-[11px]" style={{ color: 'var(--theme-text-secondary)' }}>
                  {t('generated.broadcastinline_dismiss_5770')}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
